/**
 * The shapes the search panel renders. Structurally typed so this module never imports server
 * code (the trick `modules/notifications/types.ts` and `modules/item-card/types.ts` use).
 */

export const SEARCH_CATEGORIES = [
  'all',
  'cross_boards',
  'updates',
  'files',
  'people',
  'tags',
  'docs',
] as const;
export type SearchCategoryId = (typeof SEARCH_CATEGORIES)[number];

/** monday's chip strip, verbatim and in its measured order (`[se] 01-focused-anatomy.json`). */
export const SEARCH_CATEGORY_LABELS: Record<SearchCategoryId, string> = {
  all: 'All',
  cross_boards: 'Cross Boards',
  updates: 'Updates',
  files: 'Files',
  people: 'People',
  tags: 'Tags',
  docs: 'Docs',
};

/** `Tags` and `Docs` have no store yet; the chips are rendered disabled, never hidden. */
export const SEARCH_CATEGORIES_IMPLEMENTED: readonly SearchCategoryId[] = [
  'all',
  'cross_boards',
  'updates',
  'files',
  'people',
];

export const SEARCH_QUICK_FILTERS = [
  'related_to_me',
  'assigned_to_me',
  'my_files',
  'archived_boards',
  'mentioned_me',
] as const;
export type SearchQuickFilterId = (typeof SEARCH_QUICK_FILTERS)[number];

export const SEARCH_QUICK_FILTER_LABELS: Record<SearchQuickFilterId, string> = {
  related_to_me: 'Related to me',
  assigned_to_me: "I'm assigned to",
  my_files: 'My Files',
  archived_boards: 'Archived Boards',
  mentioned_me: 'I was mentioned',
};

export type SearchKind = 'board' | 'item' | 'update' | 'file' | 'person';

export interface SearchRowView {
  kind: SearchKind;
  id: string;
  title: string;
  snippet: string;
  boardId: string | null;
  boardName: string | null;
  itemId: string | null;
  state: string;
  updatedAt: string;
  href: string;
  rank: number;
}

export interface SearchGroupView {
  kind: SearchKind;
  label: string;
  total: number;
  rows: SearchRowView[];
}

export interface SearchResultsView {
  term: string;
  category: SearchCategoryId;
  groups: SearchGroupView[];
  total: number;
  tookMs: number;
}

export interface SearchQueryInput {
  term: string;
  category: SearchCategoryId;
  quickFilters: SearchQuickFilterId[];
}

export interface SearchSource {
  query(input: SearchQueryInput): Promise<SearchResultsView>;
  recent(): Promise<string[]>;
  remember(term: string): Promise<void>;
  clearRecent(): Promise<void>;
}
